import type { ReactNode } from "react";

type Tone = "neutral" | "ember";

type EmptyStateProps = {
  /** Monospaced eyebrow above the heading. Optional. */
  eyebrow?: string;
  /** Heading line. Keep under ~6 words. */
  title: string;
  /** Body sentence. Keep under ~140 chars. */
  body?: string;
  /** Optional decorative glyph rendered top-left. Two-line max. */
  glyph?: ReactNode;
  /** Render below the body. Usually a <Link to="…" className="btn-volt ..."> or
   *  a retry button — kept as a slot so the caller owns styling and a11y. */
  action?: ReactNode;
  /** ember = error/red surface, neutral = standard surface. */
  tone?: Tone;
};

/** Empty-state card. Replaces flat "No tienes favoritos." sentences with a
 *  shape that signals "this is the expected state" instead of "the page
 *  broke." Used on /favorites, /activity, and other surfaces with realistic
 *  empty paths. Tone "ember" is for recoverable error states so the same
 *  shape works without a second component. */
export function EmptyState({
  eyebrow,
  title,
  body,
  glyph,
  action,
  tone = "neutral",
}: EmptyStateProps) {
  const surface =
    tone === "ember"
      ? "border-ember/40 bg-ember/[0.04]"
      : "border-line bg-surface";
  const titleColor = tone === "ember" ? "text-ember" : "text-fg";
  return (
    <section
      role={tone === "ember" ? "alert" : "status"}
      className={`relative overflow-hidden rounded-lg border px-5 py-6 ${surface}`}
    >
      {glyph && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-4 top-4 text-3xl text-fg-faint/70"
        >
          {glyph}
        </div>
      )}
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h2 className={`mt-2 text-lg font-semibold tracking-tight ${titleColor}`}>
        {title}
      </h2>
      {body && <p className="mt-1.5 max-w-prose text-sm text-fg-mute">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </section>
  );
}
